import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AccountContact,
  Constants,
  ErrorReportingService,
  FullPageMaskService,
  ICountry,
  ILookup,
  IUserDetails,
  SecurityService,
  strictEmailValidator,
} from 'common';
import { distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { ApplicationPermissions, Permission } from '../../../../models/application-permissions.model';
import { ContactService } from '../../../../services/contact.service';
import { MatDialog } from '@angular/material/dialog';
import { AccountEditContactSuccessComponent } from './edit-contact-success.component';

@Component({
  selector: 'acc-edit-contact',
  templateUrl: './edit-contact.component.html',
  styleUrls: ['./edit-contact.component.scss'],
})
export class EditContactComponent implements OnInit, OnDestroy {
  salutations: ILookup[];
  countries: ICountry[];
  counties: ILookup[];
  existingEmails: string[] = [];
  contact: AccountContact;
  applicationPermissions: ApplicationPermissions;
  originalPermissionSet: number;

  private _destroy$ = new Subject<void>();

  groupContact = new UntypedFormGroup({
    salutation: new UntypedFormControl('', Validators.required),
    firstName: new UntypedFormControl('', Validators.required),
    lastName: new UntypedFormControl('', Validators.required),
    positionHeld: new UntypedFormControl('', Validators.required),
    isRegulatoryContact: new UntypedFormControl(''),
    isSQUContact: new UntypedFormControl(''),
    isDirector: new UntypedFormControl(''),
    phone: new UntypedFormControl('', Validators.required),
    mobile: new UntypedFormControl(''),
    fax: new UntypedFormControl(''),
    email: new UntypedFormControl('', [
      Validators.required,
      strictEmailValidator,
      (control: AbstractControl): ValidationErrors | null =>
        this.existingEmails.includes(control.value) && control.value.length > 0 ? { emailExists: true } : null,
    ]),
  });

  groupAddress = new UntypedFormGroup({
    addressLine1: new UntypedFormControl('', Validators.required),
    addressLine2: new UntypedFormControl(''),
    addressLine3: new UntypedFormControl(''),
    addressLine4: new UntypedFormControl(''),
    postCode: new UntypedFormControl(''),
    country: new UntypedFormControl('', Validators.required),
    county: new UntypedFormControl('', Validators.required),
  });

  groupPermissions = new UntypedFormGroup({
    permissions: new UntypedFormControl(''),
  });

  user: IUserDetails;
  showErrorUpdateContact = false;
  showErrorUpdateAddress = false;
  showErrorUpdatePermissions = false;
  hasRegulatoryError = false;
  regulatoryErrorMessage = '';
  errorMessages = [];

  public get providerTypeString(): string {
    return this.contactService.getProviderTypeString();
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private security: SecurityService,
    private errorService: ErrorReportingService,
    private contactService: ContactService,
    private maskService: FullPageMaskService,
    public dialog: MatDialog
  ) {
    this.contactService.permissionChanges$.pipe(takeUntil(this._destroy$)).subscribe((newPermissions) => {
      this.contact.accountPermissions = newPermissions;
    });
    this.groupAddress.controls.country.valueChanges.pipe(distinctUntilChanged()).subscribe((country: string) => this.countryChanged(country));
  }

  ngOnInit() {
    this.user = this.security.profile.user;
    this.salutations = this.route.snapshot.data.salutations;
    this.countries = this.route.snapshot.data.countries;
    this.counties = this.route.snapshot.data.counties;
    this.contact = this.route.snapshot.data.contact;
    this.existingEmails = this.route.snapshot.data.contacts.map((x) => x.email);
    this.applicationPermissions = this.route.snapshot.data.permissions;
    this.setForm(this.route.snapshot.data.contact);

    this.groupContact.disable();
    this.groupAddress.disable();
    this.groupPermissions.disable();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  get canEditPermissions(): boolean {
    return (
      this.security.profile.user.isSuperUser ||
      (this.security.profile.selectedAccount.accountPermissions & Constants.permissionGroups.administration.canModifyOnlineAccess) === 1
    );
  }

  edit(formGroup: UntypedFormGroup) {
    formGroup.enable();

    if (formGroup === this.groupAddress) {
      this.countryChanged(this.groupAddress.controls.country.value);
    }
    if (formGroup === this.groupContact && this.groupContact.value.email.length > 0) {
      this.groupContact.controls.email.disable();
    }
  }

  back() {
    this.router.navigate(['accounts/details/contacts']);
  }

  cancel(formGroup: UntypedFormGroup) {
    if (formGroup === this.groupContact) {
      this.setContactForm(this.contact);
      this.showErrorUpdateContact = false;
    } else if (formGroup === this.groupAddress) {
      this.setAddressForm(this.contact);
      this.showErrorUpdateAddress = false;
    } else if (formGroup === this.groupPermissions) {
      if (this.applicationPermissions.groups?.length > 0) {
        const allPermissions = this.applicationPermissions.groups.reduce((acc: Permission[], val) => acc.concat(val.permissions), []);
        for (let permission of allPermissions) {
          permission.invalid = false;
        }
      }

      this.setPermissionsForm(this.contact);
      this.showErrorUpdatePermissions = false;
    }

    formGroup.disable();
  }

  countryChanged(value: string) {
    if (value.toLowerCase() === Constants.irelandCountryString.toLocaleLowerCase()) {
      this.groupAddress.controls.county.enable();
    } else {
      this.groupAddress.controls.county.patchValue('');
      this.groupAddress.controls.county.disable();
    }
  }

  canRequestPasswordReset() {
    return (
      !this.groupPermissions.disabled &&
      this.originalPermissionSet > Constants.permissionGroups.hasNoOnlineAccess &&
      this.contact.accountPermissions > Constants.permissionGroups.hasNoOnlineAccess
    );
  }

  canRequestMfaReset() {
    return (
      !this.groupPermissions.disabled &&
      this.originalPermissionSet > Constants.permissionGroups.hasNoOnlineAccess &&
      this.contact.accountPermissions > Constants.permissionGroups.hasNoOnlineAccess
    );
  }

  requestPasswordReset() {
    this.maskService.showLoading();
    this.security
      .requestPasswordReset(this.contact.email)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (_) => {
          this.errorService.displayInfo('Request sent successfully.');
        },
        error: (response) => {
          this.errorMessages = this.errorService.getResponseDTOErrors(response);
          this.errorService.displayWarning('There was a problem processing your request.');
          this.maskService.hideLoading();
        },
        complete: () => this.maskService.hideLoading(),
      });
  }

  requestMfaReset() {
    this.maskService.showLoading();
    this.security
      .requestMfaReset(this.contact.email)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (_) => {
          this.errorService.displayInfo('Request sent successfully.');
        },
        error: (response) => {
          this.errorMessages = this.errorService.getResponseDTOErrors(response);
          this.errorService.displayWarning('There was a problem processing your request.');
          this.maskService.hideLoading();
        },
        complete: () => this.maskService.hideLoading(),
      });
  }

  saveContactGroup() {
    if (!this.groupContact.valid) {
      return;
    }

    const contact = new AccountContact(
      this.contact.acCode,
      this.contact.lcAuto,

      this.groupContact.get('salutation').value,
      this.groupContact.get('firstName').value,
      this.groupContact.get('lastName').value,
      this.groupContact.get('positionHeld').value,
      this.groupContact.get('isRegulatoryContact').value,
      this.groupContact.get('isSQUContact').value,
      this.groupContact.get('isDirector').value,
      this.contact.addressLine1,
      this.contact.addressLine2,
      this.contact.addressLine3,
      this.contact.addressLine4,
      this.contact.postCode,
      this.contact.county,
      this.contact.country,

      this.groupContact.get('phone').value,
      this.groupContact.get('mobile').value,
      this.groupContact.get('fax').value,
      this.groupContact.get('email').value,

      this.contact.accountPermissions != -1,
      false,
      this.contact.accountPermissions,
      null
    );

    this.showErrorUpdateContact = false;
    this.maskService.showLoading();

    this.contactService
      .updateContact(contact)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (wasRequestOk: boolean) => {
          this.showErrorUpdateContact = !wasRequestOk;
          if (wasRequestOk) {
            this.showStatus();
          }
        },
        error: (response) => {
          this.errorMessages = this.errorService.getResponseDTOErrors(response);
          this.showErrorUpdateContact = true;
          if (this.errorMessages.length > 0) {
            this.setErrorMessages(response.error.errors);
          }
          this.maskService.hideLoading();
        },
      });
  }

  setErrorMessages(errors: any) {
    this.hasRegulatoryError = 'Regulatory' in errors;
    if (this.hasRegulatoryError) this.regulatoryErrorMessage = errors.Regulatory;
  }

  saveAddressGroup() {
    if (!this.groupAddress.valid) {
      return;
    }

    const contact = new AccountContact(
      this.contact.acCode,
      this.contact.lcAuto,

      this.contact.salutation,
      this.contact.firstName,
      this.contact.lastName,
      this.contact.positionHeld,
      this.contact.isRegulatoryContact,
      this.contact.isSQUContact,
      this.contact.isDirector,

      this.groupAddress.get('addressLine1').value,
      this.groupAddress.get('addressLine2').value,
      this.groupAddress.get('addressLine3').value,
      this.groupAddress.get('addressLine4').value,
      this.groupAddress.get('postCode').value,
      this.groupAddress.get('county').value,
      this.groupAddress.get('country').value,

      this.contact.phone,
      this.contact.mobile,
      this.contact.fax,
      this.contact.email,
      this.contact.accountPermissions != Constants.permissionGroups.hasNoOnlineAccess,
      false,
      this.contact.accountPermissions,
      null
    );

    this.showErrorUpdateAddress = false;
    this.maskService.showLoading();

    this.contactService
      .updateContact(contact)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (wasRequestOk: boolean) => {
          this.showErrorUpdateAddress = !wasRequestOk;
          if (wasRequestOk) {
            this.showStatus();
          }
        },
        error: (response) => {
          this.errorMessages = this.errorService.getResponseDTOErrors(response);
          this.showErrorUpdateAddress = true;
          this.maskService.hideLoading();
        },
      });
  }

  savePermissionsGroup() {
    if (!this.groupPermissions.valid) {
      return;
    }

    const contact = new AccountContact(
      this.contact.acCode,
      this.contact.lcAuto,

      this.contact.salutation,
      this.contact.firstName,
      this.contact.lastName,
      this.contact.positionHeld,
      this.contact.isRegulatoryContact,
      this.contact.isSQUContact,
      this.contact.isDirector,

      this.contact.addressLine1,
      this.contact.addressLine2,
      this.contact.addressLine3,
      this.contact.addressLine4,
      this.contact.postCode,
      this.contact.county,
      this.contact.country,

      this.contact.phone,
      this.contact.mobile,
      this.contact.fax,
      this.contact.email,

      this.contact.accountPermissions != -1,
      false,
      this.contact.accountPermissions,
      null
    );

    this.showErrorUpdatePermissions = false;
    this.maskService.showLoading();

    this.contactService
      .updateContact(contact)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (wasRequestOk: boolean) => {
          this.showErrorUpdatePermissions = !wasRequestOk;
          if (wasRequestOk) {
            this.showStatus();
          } else {
            this.maskService.hideLoading();
          }
        },
        error: (response) => {
          this.errorMessages = this.errorService.getResponseDTOErrors(response);
          this.showErrorUpdatePermissions = true;
          this.maskService.hideLoading();
        },
      });
  }

  showStatus(): void {
    this.errorService.displayInfo('The contact was updated successfully');

    const dialogRef = this.dialog.open(AccountEditContactSuccessComponent);
    dialogRef.afterClosed().subscribe((result) => {
      this.router.navigateByUrl('/accounts/details/contacts');
    });
  }

  private setForm(contact: AccountContact) {
    this.setContactForm(contact);
    this.setAddressForm(contact);
    this.setPermissionsForm(contact);
  }

  private setContactForm(contact: AccountContact) {
    this.groupContact.get('salutation').setValue(contact.salutation);
    this.groupContact.get('firstName').setValue(contact.firstName);
    this.groupContact.get('lastName').setValue(contact.lastName);
    this.groupContact.get('positionHeld').setValue(contact.positionHeld);
    this.groupContact.get('isRegulatoryContact').setValue(contact.isRegulatoryContact);
    this.groupContact.get('isSQUContact').setValue(contact.isSQUContact);
    this.groupContact.get('isDirector').setValue(contact.isDirector);
    this.groupContact.get('phone').setValue(contact.phone);
    this.groupContact.get('mobile').setValue(contact.mobile);
    this.groupContact.get('fax').setValue(contact.fax);
    this.groupContact.get('email').setValue(contact.email);
  }

  private setAddressForm(contact: AccountContact) {
    this.groupAddress.get('addressLine1').setValue(contact.addressLine1);
    this.groupAddress.get('addressLine2').setValue(contact.addressLine2);
    this.groupAddress.get('addressLine3').setValue(contact.addressLine3);
    this.groupAddress.get('addressLine4').setValue(contact.addressLine4);
    this.groupAddress.get('postCode').setValue(contact.postCode);
    this.groupAddress.get('country').setValue(contact.country);
    this.groupAddress.get('county').setValue(contact.county);
  }

  private setPermissionsForm(contact: AccountContact) {
    this.originalPermissionSet = contact.accountPermissions;
    this.groupPermissions.get('permissions').setValue(contact.accountPermissions);
  }
}
